import MobileOutlined from '@ant-design/icons/lib/icons/MobileOutlined';
import { default as Form } from 'antd/es/form';
import { default as Input } from 'antd/es/input';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import {
  usePostAuthenticationMfaRequestSms,
  usePostAuthenticationMfaVerifySms
} from 'api';

import { setCredentials } from 'reduxLocal/slices/authSlice';

import { useAccessToken } from 'hooks/context';
import { useMfaSettings } from 'hooks/useMfaSettings';
import { useRedirectTo } from 'hooks/useRedirectTo';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { GoBackButton } from '@atoms/GoBackButton';

/**
 * @deprecated: supported code, but deprecated feature in favor of TOTPValidate
 */
export const SmsValidate = ({ onSuccess }: { onSuccess: () => void }) => {
  const [form] = Form.useForm();
  const { setAccessToken } = useAccessToken();
  const openNotification = useOpenNotification();
  const dispatch = useDispatch();

  const { navigateWithRedirectTo } = useRedirectTo();
  const { totpEnabled, loading, hasPhone } = useMfaSettings();
  const navigate = useNavigate();

  const { mutate: requestSms } = usePostAuthenticationMfaRequestSms();
  const {
    mutate: validateSms,
    isPending: validationPending,
    error: validateSmsError
  } = usePostAuthenticationMfaVerifySms({
    request: { withCredentials: true }
  });

  const triggerSmsRequest = useCallback(() => {
    requestSms(undefined, {
      onSuccess: () =>
        openNotification({
          kind: 'success',
          message: 'SMS has been sent'
        }),
      onError: (err) => {
        if (err.error) {
          openNotification({
            kind: 'error',
            message: err.error
          });
        }
      }
    });
  }, [openNotification, requestSms]);

  useEffect(() => {
    triggerSmsRequest();
  }, []);

  const handleAccessToken = (accessToken: string) => {
    dispatch(setCredentials({ access_token: accessToken }));
    setAccessToken(accessToken);
    onSuccess();
  };

  const onFinish = (values: { code: string }) => {
    validateSms(
      { data: { auth_payload: values.code } },
      {
        onSuccess: (data) => {
          const accessToken = data?.data?.access_token;
          if (hasValue(accessToken)) {
            handleAccessToken(accessToken);
          }
        }
      }
    );
  };

  return (
    <div className="align-center flex flex-col">
      <GoBackButton
        onClick={() => {
          // if this user only has the SMS option, redirect to login page
          if (!totpEnabled && hasPhone && !loading) {
            navigateWithRedirectTo('/');
          } else {
            navigate(-1);
          }
        }}
      />

      <div className="flex">
        <div>
          <h1 className="text-2xl font-medium md:pb-2 md:text-4xl">
            <MobileOutlined /> SMS Verification
          </h1>

          <a
            className="cursor-pointer font-medium text-content-link hover:text-content-brand hover:underline"
            onClick={triggerSmsRequest}
          >
            Request sms again
          </a>
        </div>
      </div>

      <Form form={form} onFinish={onFinish} clearOnDestroy>
        <div className="flex flex-row flex-wrap gap-2 py-4">
          <div className="flex-col">
            <Form.Item name="code">
              <Input
                style={{ height: 48, width: 206 }}
                size="large"
                placeholder="SMS Token"
              />
            </Form.Item>
            {validateSmsError && (
              <div className="mt-2 text-sm text-red-500">
                {validateSmsError.error}
              </div>
            )}
          </div>
          <div className="flex-col">
            <Form.Item>
              <PrimaryButton
                style={{ height: 48, width: 124 }}
                htmlType="submit"
                loading={validationPending}
                size="large"
                block
              >
                Verify
              </PrimaryButton>
            </Form.Item>
          </div>
        </div>
      </Form>
    </div>
  );
};
